import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import { Box, Menu, Tooltip } from "@mui/material";
import { CHAT_REACTION_EMOJI, type ChatReactionSummary } from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";

import { reactionTooltip } from "./reactionLabel.js";

/**
 * Emoji reactions, WhatsApp-style: a compact pill that **hangs off the bubble's
 * bottom edge** (not a row inside the message), offset to the message's side —
 * bottom-right for my messages, bottom-left for others. Each reacted emoji is a
 * count chip (highlighted when I'm a reactor); a hover-revealed "+" opens the
 * fixed emoji palette. Clicking a chip or a palette emoji toggles it. Hovering
 * a chip names WHO reacted (`reactionLabel.ts`), also exposed as the chip's
 * accessible name so it isn't mouse-only.
 *
 * Absolutely positioned against the bubble (`position: relative` on the bubble),
 * floating over the wallpaper with its own background/shadow. The bubble grants
 * extra bottom margin when reactions exist so the pill never overlaps the next
 * message; the add-"+" is revealed by the bubble's hover rule.
 */
export function ReactionBar({
  reactions,
  selfId,
  mine,
  onReact,
}: {
  reactions: ChatReactionSummary[];
  selfId: number;
  mine: boolean;
  onReact: (emoji: string) => void;
}): JSX.Element {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: -13,
        ...(mine ? { right: 6 } : { left: 6 }),
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        gap: 0.4,
      }}
    >
      {reactions.map((r) => {
        const reacted = r.user_ids.includes(selfId);
        // Who tapped this emoji — on hover AND on the accessible name, so the
        // information isn't mouse-only.
        const who = reactionTooltip(r, selfId);
        return (
          <Tooltip key={r.emoji} title={who} placement="top" arrow enterTouchDelay={0}>
            <Box
              component="button"
              type="button"
              data-testid={`chat-reaction-${r.emoji}`}
              aria-pressed={reacted}
              aria-label={who}
              onClick={() => onReact(r.emoji)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.3,
                height: 20,
                px: 0.6,
                borderRadius: 999,
                border: 1,
                borderColor: reacted ? "chat.accent" : "divider",
                bgcolor: reacted ? "chat.headerBg" : "background.paper",
                boxShadow: 1, // float over the wallpaper
                color: "text.primary",
                cursor: "pointer",
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              <span>{r.emoji}</span>
              <Box
                component="span"
                sx={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: "chat.meta" }}
              >
                {r.count}
              </Box>
            </Box>
          </Tooltip>
        );
      })}

      <Box
        component="button"
        type="button"
        className="chat-react-add"
        aria-label="Add reaction"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height: 20,
          width: 20,
          borderRadius: 999,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: 1,
          color: "chat.meta",
          cursor: "pointer",
          opacity: 0,
          transition: "opacity 150ms",
          "&:hover": { color: "chat.accent" },
        }}
      >
        <AddReactionOutlinedIcon sx={{ fontSize: 15 }} />
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <Box sx={{ display: "flex", gap: 0.25, px: 0.75, py: 0.25 }}>
          {CHAT_REACTION_EMOJI.map((emoji) => (
            <Box
              key={emoji}
              component="button"
              type="button"
              aria-label={`React ${emoji}`}
              onClick={() => {
                onReact(emoji);
                setAnchor(null);
              }}
              sx={{
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                fontSize: 20,
                lineHeight: 1,
                p: 0.5,
                borderRadius: 1,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {emoji}
            </Box>
          ))}
        </Box>
      </Menu>
    </Box>
  );
}
